/**
 * Custom hook para manejar el estado de la vista Forecast Metrics
 * Centraliza toda la lógica de estado y event handlers
 */

import { useState } from 'react';
import { 
  FORECAST_METRICS_DEFAULT_VALUES,
  getForecastRightLayer,
  getForecastModelDisplayName,
  getForecastMetricsName
} from '../config/forecastMetrics.js';
import {
  getYesterdayFormatted,
  calculateForecastDateRange,
  getForecastWeekNumber,
  formatForecastDate,
  combineForecastDateTime
} from '../utils/forecastMetricsDateUtils.js';

/**
 * Hook personalizado para gestionar el estado de Forecast Metrics
 * @returns {Object} Estado y funciones para manejar la vista
 */
export const useForecastMetricsState = () => {
  // Estados principales
  const [cqlSevere, setCqlSevere] = useState(FORECAST_METRICS_DEFAULT_VALUES.cqlSevere);
  const [cqlFecha, setCqlFecha] = useState(getYesterdayFormatted());
  const [selectedModel, setSelectedModel] = useState(FORECAST_METRICS_DEFAULT_VALUES?.selectedModel || 'CNN_HISTORICAL:EU_025_live_demo');
  const [selectedRisk, setSelectedRisk] = useState(FORECAST_METRICS_DEFAULT_VALUES.selectedRisk);
  const [tabIndex, setTabIndex] = useState(FORECAST_METRICS_DEFAULT_VALUES.tabIndex);

  // Estados de capas de mapas
  const [mapLeftLayer, setMapLeftLayer] = useState(FORECAST_METRICS_DEFAULT_VALUES.selectedModel);
  const [mapCrossborderLayer, setMapCrossborderLayer] = useState(null);

  // Estados de tiempo y fecha
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [issueTimeOptions, setIssueTimeOptions] = useState([]);

  // Estados de UI
  const [anchorEl, setAnchorEl] = useState(null);
  const [backdropOpen, setBackdropOpen] = useState(false);

  // Valores calculados
  const dateRange = calculateForecastDateRange(cqlFecha);
  const weekNumber = getForecastWeekNumber(cqlFecha);
  const prettyModelName = getForecastModelDisplayName(selectedModel);
  const metricsName = getForecastMetricsName(selectedModel);
  // Derivada, no estado+efecto: el layout (mapa único en turbulence) cambia en el mismo render que la capa
  // izquierda, así el fitBounds de MapLeft ya ve el tamaño final del mapa
  const mapRightLayer = getForecastRightLayer(mapLeftLayer);

  // Derivado, no estado+efecto: el issue cambia en el mismo render que el día y la hora elegidos. Con el efecto iba un
  // render por detrás de cqlFecha y ese render pedía el día nuevo con el issue viejo (pmtiles 404, WFS con otro issue)
  const issueTime = combineForecastDateTime(selectedDate, selectedTime);

  // Event handlers
  const handleDateChange = (fecha) => {
    const formattedDate = formatForecastDate(fecha);
    const day = formattedDate.slice(0, 10);
    if (day !== cqlFecha.slice(0, 10)) {
      // Otro día en el calendario: D-0 de ese día y la hora de su issue, que es donde se recoloca el slider. Fecha e
      // issue cambian a la vez, así no sale ninguna petición con la combinación vieja
      setSelectedDate(day);
      setCqlFecha(`${day} ${selectedTime || '00:00:00'}`);
      return;
    }
    setCqlFecha(formattedDate);
  };

  const handleModelClick = (value) => {
    setSelectedModel(value);
    setMapLeftLayer(value);
    // Solo visibility va cada 30 min: las capas horarias no tienen la media hora (el slider hace lo mismo con su posición)
    if (!value.includes('VISIBILITY')) setCqlFecha((fecha) => fecha.replace(/:30:00$/, ':00:00'));
  };

  const handleRiskClick = (value) => {
    setSelectedRisk(value);
    setCqlSevere(value);
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
  };

  // Botón del aviso "Data available for ..." del mapa: el día válido `day` con el issue `issueDay` + `hour` (D-0 por
  // defecto; D-1/D-2 cuando el día solo se ve con un run anterior). Día, issue y hora a la vez, como handleDateChange,
  // para que no salga ninguna petición con la combinación vieja. Con D-1/D-2 el slider arranca a las 00:00 del día
  const goToIssue = (day, hour, issueDay = day) => {
    setSelectedDate(issueDay);
    setSelectedTime(`${hour}:00:00`);
    setCqlFecha(`${day} ${issueDay === day ? `${hour}:00:00` : '00:00:00'}`);
  };

  const handleFilterChange = (event, value) => {
    setCqlSevere(value || 'Light');
  };

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  // Handlers para backdrop
  const handleBackdropOpen = () => {
    setBackdropOpen(true);
  };

  const handleBackdropClose = () => {
    setBackdropOpen(false);
  };

  // Handlers para menú
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const selectorCapasDuales = (layerName) => {
    setMapLeftLayer(layerName);
    handleClose();
  };

  return {
    // Estados
    cqlSevere,
    cqlFecha,
    selectedModel,
    selectedRisk,
    tabIndex,
    mapLeftLayer,
    mapRightLayer,
    mapCrossborderLayer,
    issueTime,
    selectedDate,
    selectedTime,
    issueTimeOptions,
    anchorEl,
    backdropOpen,
    
    // Valores calculados
    cqlFechaMin: dateRange.cqlFechaMin,
    cqlFechaMax: dateRange.cqlFechaMax,
    weekNumber,
    prettyModelName,
    metricsName,
    
    // Event handlers
    handleDateChange,
    handleModelClick,
    handleRiskClick,
    handleDateSelect,
    handleTimeSelect,
    goToIssue,
    handleFilterChange,
    handleTabChange,
    handleBackdropOpen,
    handleBackdropClose,
    handleClick,
    handleClose,
    selectorCapasDuales,
    
    // Setters para casos especiales
    setIssueTimeOptions,
    setCqlSevere,
    setSelectedModel,
    setSelectedRisk,
    setTabIndex,
  };
};
